import { DropdownFilter } from '@cartwheel/components'
import { formatHour, HOURS } from './utils'

interface FilterItem {
  id: string
  name: string
}

interface TimeFilterProps {
  startHour: number
  endHour: number
  onStartHourChange: (hour: number) => void
  onEndHourChange: (hour: number) => void
}

export function TimeFilter({
  startHour,
  endHour,
  onStartHourChange,
  onEndHourChange,
}: TimeFilterProps) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-sm text-gray-600 whitespace-nowrap">Session start time:</span>
      <select
        value={startHour}
        onChange={(e) => onStartHourChange(Number(e.target.value))}
        className="text-sm border border-gray-stroke rounded px-2 py-1 text-gray-700 bg-white w-24"
        aria-label="Session start time from"
      >
        {HOURS.map((h) => (
          <option key={h} value={h}>
            {formatHour(h)}
          </option>
        ))}
      </select>
      <span className="text-sm text-gray-500">to</span>
      <select
        value={endHour}
        onChange={(e) => onEndHourChange(Number(e.target.value))}
        className="text-sm border border-gray-stroke rounded px-2 py-1 text-gray-700 bg-white w-24"
        aria-label="Session start time to"
      >
        {HOURS.map((h) => (
          <option key={h} value={h}>
            {formatHour(h)}
          </option>
        ))}
      </select>
    </div>
  )
}

interface DropdownFiltersProps {
  showSchoolDropdown: boolean
  institutions: FilterItem[]
  students: FilterItem[]
  clinicians: string[]
  month: number
  year: number
  onInstitutionChange: (ids: string[]) => void
  onStudentChange: (ids: string[] | null) => void
  onClinicianChange: (names: string[] | null) => void
}

export function DropdownFilters({
  showSchoolDropdown,
  institutions,
  students,
  clinicians,
  month,
  year,
  onInstitutionChange,
  onStudentChange,
  onClinicianChange,
}: DropdownFiltersProps) {
  if (!showSchoolDropdown && students.length <= 1 && clinicians.length <= 1) {
    return null
  }

  return (
    <div className="flex flex-wrap items-center gap-3 mb-3">
      {showSchoolDropdown && institutions.length > 1 ? (
        <DropdownFilter
          key={`school-${month}-${year}`}
          label="School"
          items={institutions.map((inst) => ({ value: inst.id, label: inst.name }))}
          defaultValue={institutions.map((inst) => inst.id)}
          onChangeImmediate={onInstitutionChange}
          showViewModeToggle={false}
          popoverMinWidth="325px"
        />
      ) : !showSchoolDropdown && institutions.length > 0 ? (
        <span className="text-sm font-medium text-gray-700">{institutions[0].name}</span>
      ) : null}
      {students.length > 1 && (
        <DropdownFilter
          key={`student-${month}-${year}`}
          label="Student"
          items={students.map((s) => ({ value: s.id, label: s.name }))}
          defaultValue={students.map((s) => s.id)}
          onChangeImmediate={(ids) => onStudentChange(ids.length === students.length ? null : ids)}
          showViewModeToggle={false}
          popoverMinWidth="250px"
        />
      )}
      {clinicians.length > 1 && (
        <DropdownFilter
          key={`clinician-${month}-${year}`}
          label="Clinician"
          items={clinicians.map((c) => ({ value: c, label: c }))}
          defaultValue={clinicians}
          onChangeImmediate={(names) =>
            onClinicianChange(names.length === clinicians.length ? null : names)
          }
          showViewModeToggle={false}
          popoverMinWidth="250px"
        />
      )}
    </div>
  )
}
